import React from "react";
import { DatePickerWithSlotsDay } from "./DatePickerDay";
import { FormattedMessage } from "react-intl";
import { addDays, differenceInWeeks, endOfMonth, endOfWeek, getMonth, startOfMonth, startOfWeek } from "date-fns";
import type { InvalidPickupDate } from "../../server/StoreInfoRequests";
import type { PickupDates } from "../../stores/DatesProvider";
import { calculateLastDay } from "./DatePickerUtil";
import { formatLocalDate, getStartDayOfWeek, parseDate } from "../../utils/DateUtil";
const MONTH_I18N_KEYS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const DAY_SHORT_I18N_KEYS = ["SUN_SHORT", "MON_SHORT", "TUE_SHORT", "WED_SHORT", "THU_SHORT", "FRI_SHORT", "SAT_SHORT"];

/* Generic Date Picker Month */
type DatePickerMonthProps = {
	year: number;
	month: number;
	dateFormat: string;
	renderDay: (date: Date, key: string) => React.ReactNode;
};
export const DatePickerMonth = ({ year, month, dateFormat, renderDay }: DatePickerMonthProps) => {
	const weekRows = getDatesOfWeeks(year, month, dateFormat).map((weekDates) => (
		<div key={`${year}:${month}:${weekDates[0]}`} className="mfcss_datepicker_dayrow">
			{weekDates.map((dateString) => {
				const date = parseDate(dateString);

				if (getMonth(date) !== month) {
					return <div key={dateString} className="not-in-month disabled" />;
				}

				return renderDay(date, dateString);
			})}
		</div>
	));
	return (
		<React.Fragment>
			<div
				className="mfcss_datepicker_header"
				style={{
					position: "relative",
				}}
			>
				<div className="mfcss_datepicker_monthname">
					<FormattedMessage id={MONTH_I18N_KEYS[month]} />
					&nbsp;
					{year}
				</div>
			</div>
			<DatePickerWeekdays />
			{weekRows}
		</React.Fragment>
	);
};
export const DatePickerWeekdays = () => (
	<div className="mfcss_datepicker_dayrow mfcss_datepicker_weekdays">
		{[...Array(7).keys()].map((i) => (
			<div
				key={i}
				style={{
					position: "relative",
				}}
			>
				<FormattedMessage id={DAY_SHORT_I18N_KEYS[(i + getStartDayOfWeek()) % 7]} />
			</div>
		))}
	</div>
);

function getDatesOfWeeks(year: number, month: number, dateFormat: string): string[][] {
	const monthOfYear = new Date(year, month, 1);
	const startDate = startOfWeek(startOfMonth(monthOfYear), { weekStartsOn: getStartDayOfWeek() });
	const endDate = endOfWeek(endOfMonth(monthOfYear), { weekStartsOn: getStartDayOfWeek() });
	const weeksDiff: number = differenceInWeeks(endDate, startDate);
	const offsets: number[][] = [...Array(weeksDiff + 1).keys()].map((weekNumber) =>
		[...Array(7).keys()].map((dayNumber) => weekNumber * 7 + dayNumber),
	);
	return offsets.map((week) => week.map((offset) => formatLocalDate(addDays(startDate, offset), dateFormat)));
}

/* Pickup Date Picker Month */
type PickupDatePickerMonthProps = {
	dates: PickupDates;
	year: number;
	month: number;
	selectedDate?: Date | null;
	onSelectDate?: (date: Date) => void;
	onPrevMonth?: (() => void) | null;
	onNextMonth?: (() => void) | null;
	dateFormat: string;
	disableDayByTimeSlot?: boolean;
	isPickup?: boolean;
};
const missingPickupDateInfo: InvalidPickupDate = {
	type: "InvalidDate",
	isStoreHoliday: false,
};
export const DatePickerWithSlotsMonth = ({
	dates,
	selectedDate,
	onSelectDate,
	dateFormat,
	disableDayByTimeSlot,
	isPickup,
	...restProps
}: PickupDatePickerMonthProps) => {
	const renderDay = (date: Date, key: string) => (
		<DatePickerWithSlotsDay
			key={key}
			date={date}
			info={dates.get(formatLocalDate(date, dateFormat)) || missingPickupDateInfo}
			lastValidDate={calculateLastDay(dateFormat)}
			isSelected={!!selectedDate && selectedDate.getTime() === date.getTime()}
			onSelectDate={onSelectDate}
			disableDayByTimeSlot={disableDayByTimeSlot}
			isPickup={!!isPickup}
		/>
	);

	return <DatePickerMonth {...restProps} dateFormat={dateFormat} renderDay={renderDay} />;
};
